import type React from 'react';
import type { BombiixIconProps } from '../../../../../bombiix-icon/types';
import {
  CalendarIcon,
  CheckBoxIcon,
  ChevronIcon,
  CommentsIcon,
  DesignDetailsIcon,
  DurationCounterIcon,
  FormulaIcon,
  ImageIcon,
  RadioButtonIcon,
  SourceIcon,
  StatusActiveIcon,
  TableViewIcon,
  TextboxIcon,
} from '../../../../../bombiix-icon';
import type { CMField, CMSection, CMSubSection, CMTable } from '../add-edit.types';
import { FieldKind } from './system-canvas-cm.types';
import { MAX_WIDTH_PCT, WIDTH_DECIMAL_FACTOR } from './constants';

export const getIconForKind = (kind: FieldKind): React.FC<BombiixIconProps> | null => {
  switch (kind) {
    case 'dorpdown':
      return ChevronIcon;
    case 'text':
      return TextboxIcon;
    case 'radio':
      return RadioButtonIcon;
    case 'checkbox':
      return CheckBoxIcon;
    case 'table':
      return TableViewIcon;
    case 'date-picker':
      return CalendarIcon;
    case 'time-picker':
      return DurationCounterIcon;
    case 'comment':
      return CommentsIcon;
    case 'status':
      return StatusActiveIcon;
    case 'source':
      return SourceIcon;
    case 'image':
      return ImageIcon;
    case 'calculation':
      return FormulaIcon;
    case 'canvas':
      return DesignDetailsIcon;
    default:
      return null;
  }
};

export const getLabelForKind = (kind: FieldKind): string => {
  switch (kind) {
    case 'dorpdown':
      return 'Dropdown';
    case 'text':
      return 'Textbox';
    case 'radio':
      return 'Radio Button';
    case 'checkbox':
      return 'Checkbox';
    case 'table':
      return 'Table';
    case 'date-picker':
      return 'Date Picker';
    case 'time-picker':
      return 'Time Picker';
    case 'comment':
      return 'Comment';
    case 'status':
      return 'Status';
    case 'source':
      return 'Source';
    case 'image':
      return 'Image';
    case 'calculation':
      return 'Calculation';
    case 'canvas':
      return 'Canvas';
    default:
      return String(kind);
  }
};

export const collectItemsForSubSection = (sub?: CMSubSection | null): Array<{ kind: FieldKind; index: number }> => {
  const inner: Array<CMField | CMTable> = sub?.content ?? [];
  const isTable = (it: CMField | CMTable): it is CMTable => (it as CMTable).columns !== undefined;
  return inner.map((it, idx) => (isTable(it) ? { kind: 'table', index: idx } : { kind: it.type, index: idx }));
};

export const collectKindsForSection = (section?: CMSection | null): FieldKind[] => {
  const subSections: CMSubSection[] = section?.content ?? [];
  const kinds: FieldKind[] = [];
  subSections.forEach((sub) => {
    const inner: Array<CMField | CMTable> = sub?.content ?? [];
    inner.forEach((it) => {
      if ((it as CMTable).columns !== undefined) kinds.push('table');
      else kinds.push((it as CMField).type);
    });
  });
  return kinds;
};

/**
 * Rounds a width percentage to two decimal places, absorbing floating-point drift.
 * @param pct - raw percentage value.
 * @returns the percentage rounded to two decimals.
 */
export const roundWidthPct = (pct: number): number => Math.round(pct * WIDTH_DECIMAL_FACTOR) / WIDTH_DECIMAL_FACTOR;

/**
 * Checks whether a summed width total exceeds 100% once rounded to two decimals.
 * @param totalPct - summed width percentages.
 * @returns true when the rounded total is above 100.
 */
export const isWidthTotalOverLimit = (totalPct: number): boolean => roundWidthPct(totalPct) > MAX_WIDTH_PCT;

/**
 * Splits 100% across a number of fields at two decimal places; the last field takes the rounding remainder so the total is exactly 100.
 * @param count - number of fields sharing the row.
 * @returns one width string per field, e.g. six fields give five '16.66%' and one '16.7%'.
 */
export const distributeEqualWidths = (count: number): string[] => {
  if (count <= 0) return [];
  const baseWidth = Math.floor((MAX_WIDTH_PCT / count) * WIDTH_DECIMAL_FACTOR) / WIDTH_DECIMAL_FACTOR;
  const lastWidth = roundWidthPct(MAX_WIDTH_PCT - baseWidth * (count - 1));
  return Array.from({ length: count }, (_unused, index) => `${index === count - 1 ? lastWidth : baseWidth}%`);
};

/**
 * Tells whether a field type always spans the whole row, so it has no width setting and takes no share of the row's 100%.
 * @param fieldType - the field's control type.
 * @returns true for comment, canvas and table fields.
 */
export const isFullWidthFieldType = (fieldType: string | undefined): boolean => FULL_WIDTH_FIELD_TYPES.includes(fieldType ?? '');

const FULL_WIDTH_FIELD_TYPES: ReadonlyArray<string> = ['comment', 'canvas', 'table'];
